import React, { Suspense } from 'react';
import { ApolloError, NetworkStatus } from '@apollo/client';
import { Await, useFetcher, useLoaderData } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  BookmarkIcon as PinIcon,
  BuildingOffice2Icon,
  FolderIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
  SparklesIcon,
  UserIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { BookmarkIcon as PinIconSolid } from '@heroicons/react/24/solid';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { type ColumnDef, DataTable, type SortDirection } from '@oysterjs/ui/data-table';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { FilterBar, type FilterConfig } from '@oysterjs/ui/filter-bar';
import { ErrorMessage, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';
import { Spinner } from '@oysterjs/ui/spinner';
import { Switch } from '@oysterjs/ui/switch';
import { TableHeader } from '@oysterjs/ui/table';
import { Textarea } from '@oysterjs/ui/textarea';
import clsx from 'clsx';

import {
  Action,
  LoggedInAgencyUserFieldsFragment,
  MemoryCategory,
  MemoryListFieldsFragment,
  MemoryScope,
  MemorySearchFilterInput,
  MemorySurface,
  MemoryType,
  Resource,
  Scope,
  useMemoryQuery,
  useSearchMemoriesQuery,
  useUpdateMemoryMutation
} from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import {
  InfiniteScrollFooter,
  useDebouncedValue,
  useInfiniteScroll
} from '~/hooks/use-list-controls';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { useFloatingSelection } from '~/components/list/use-floating-selection-bar';

const MEMORY_PAGE_SIZE = 25;
const MEMORY_CONTENT_MAX_LENGTH = 600;
const CROSS_SURFACE = 'CROSS_SURFACE' as const;

type MemorySurfaceFilter = MemorySurface | typeof CROSS_SURFACE;
type PinFilter = 'PINNED' | 'UNPINNED';
type MemorySortKey = 'type';
type MemoryGroupKey = 'type';
type MemoryBulkOperation = {
  kind: 'pin' | 'delete' | 'promote' | 'demote';
  ids: string[];
  filterGeneration: number;
};

export type MemoryFilterState = {
  query: string;
  scopes: MemoryScope[];
  types: MemoryType[];
  categories: MemoryCategory[];
  surfaces: MemorySurfaceFilter[];
  pinned?: PinFilter;
};

export function buildMemorySearchFilter(
  state: MemoryFilterState
): MemorySearchFilterInput | undefined {
  const filter: MemorySearchFilterInput = {};
  const query = state.query.trim();
  if (query) filter.query = query;
  if (state.scopes.length > 0) filter.scopes = state.scopes;
  if (state.types.length > 0) filter.types = state.types;
  if (state.categories.length > 0) filter.categories = state.categories;

  const surfaces = state.surfaces.filter(
    (surface): surface is MemorySurface => surface !== CROSS_SURFACE
  );
  if (surfaces.length > 0) filter.surfaces = surfaces;
  if (state.surfaces.includes(CROSS_SURFACE)) filter.includeCrossSurface = true;

  if (state.pinned === 'PINNED') filter.pinned = true;
  if (state.pinned === 'UNPINNED') filter.pinned = false;

  return Object.keys(filter).length > 0 ? filter : undefined;
}

const enumLabel = (value: string) =>
  value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const memoryDateFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

const formatMemoryDate = (value: Date | string) => memoryDateFormatter.format(new Date(value));

export function groupMemoriesByType(
  memories: MemoryListFieldsFragment[],
  key: MemoryGroupKey
): { label: string; items: MemoryListFieldsFragment[] }[] {
  if (key !== 'type') return [{ label: '', items: memories }];

  const order = [MemoryType.Preference, MemoryType.Correction, MemoryType.Procedure];
  return order
    .map((type) => ({
      label: enumLabel(type),
      items: memories.filter((memory) => memory.type === type)
    }))
    .filter((group) => group.items.length > 0);
}

const sortMemories = (
  memories: MemoryListFieldsFragment[],
  key: MemorySortKey,
  direction: SortDirection
) => {
  if (key !== 'type') return memories;
  return [...memories].sort((a, b) => {
    const result = a.type.localeCompare(b.type);
    return direction === 'asc' ? result : -result;
  });
};

type MemoryLoaderData = {
  data: Promise<{ agencyUser: LoggedInAgencyUserFieldsFragment }>;
};

export const SettingsMemoryPage = () => {
  const loaderData = useLoaderData() as MemoryLoaderData;

  return (
    <Suspense fallback={<MemoryPageLoader />}>
      <Await resolve={loaderData.data}>
        {({ agencyUser }) => (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
          >
            <MemoryEnabledCard agencyUser={agencyUser} />
            <MemoryManagementTable />
          </motion.div>
        )}
      </Await>
    </Suspense>
  );
};

const MemoryEnabledCard = ({ agencyUser }: { agencyUser: LoggedInAgencyUserFieldsFragment }) => {
  const fetcher = useFetcher();
  const persistedEnabled = !agencyUser?.preferences?.memoryDisabled;
  const pendingJSON = fetcher.json as { memoryDisabled?: boolean } | undefined;
  const enabled =
    pendingJSON?.memoryDisabled !== undefined ? !pendingJSON.memoryDisabled : persistedEnabled;

  const onChange = (next: boolean) => {
    fetcher.submit(
      { memoryDisabled: !next },
      {
        method: 'PUT',
        action: '/app/home/preferences',
        encType: 'application/json'
      }
    );
  };

  return (
    <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow dark:divide-neutral-700 dark:bg-neutral-800">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-neutral-100">
              Memory
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              When enabled, Cara injects relevant saved memories into your conversations. When
              disabled, your memories remain available here but are not used.
            </p>
          </div>
          <div className="shrink-0">
            <Switch color="sky" checked={enabled} onChange={onChange} />
          </div>
        </div>
      </div>
    </div>
  );
};

const CATEGORY_COLORS: Record<
  MemoryCategory,
  'blue' | 'purple' | 'green' | 'amber' | 'rose' | 'cyan' | 'zinc'
> = {
  [MemoryCategory.Carrier]: 'amber',
  [MemoryCategory.Coverage]: 'blue',
  [MemoryCategory.Documentation]: 'cyan',
  [MemoryCategory.Workflow]: 'zinc',
  [MemoryCategory.Communication]: 'purple',
  [MemoryCategory.General]: 'zinc'
};

const SURFACE_COLORS: Record<MemorySurface, 'blue' | 'purple' | 'green'> = {
  [MemorySurface.EmailAi]: 'blue',
  [MemorySurface.Copilot]: 'purple',
  [MemorySurface.Voice]: 'green'
};

const ScopeBadge = ({ scope }: { scope: MemoryScope }) =>
  scope === MemoryScope.Agent ? (
    <Badge color="sky">Your memory</Badge>
  ) : (
    <Badge color="purple">Organization</Badge>
  );

const MemoryEmptyState = (props: { filtered: boolean }) => (
  <div className="px-6 py-12 text-center">
    {props.filtered ? (
      <MagnifyingGlassIcon className="mx-auto size-10 text-neutral-400 dark:text-neutral-500" />
    ) : (
      <SparklesIcon className="mx-auto size-10 text-neutral-400 dark:text-neutral-500" />
    )}
    <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
      {props.filtered ? 'No memories match these filters' : 'No memories yet'}
    </h4>
    <p className="mx-auto mt-1 max-w-md text-sm text-neutral-500 dark:text-neutral-400">
      {props.filtered
        ? 'Try a different search or remove one of the filters.'
        : 'Memories appear here as Cara learns preferences from conversations, or when you ask Cara to remember something.'}
    </p>
  </div>
);

const MemoryLoadError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="px-6 py-12 text-center">
    <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
      Unable to load memories
    </h4>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
      Check your connection and try again.
    </p>
    <Button className="mt-4" outline onClick={onRetry}>
      Try again
    </Button>
  </div>
);

export const MemoryManagementTable = () => {
  const { hasScopedPermission } = usePermissions();
  const selection = useFloatingSelection();
  const bulkFetcher = useFetcher<{ failedIds?: string[] }>();
  const pendingBulkOperationRef = React.useRef<MemoryBulkOperation>();
  const bulkRequestStartedRef = React.useRef(false);
  const canUpdateOrgMemories = hasScopedPermission(
    Scope.Organization,
    Resource.Memory,
    Action.Update
  );
  const canDeleteOrgMemories = hasScopedPermission(
    Scope.Organization,
    Resource.Memory,
    Action.Delete
  );

  const [search, setSearch] = React.useState('');
  const [scopes, setScopes] = React.useState<MemoryScope[]>([]);
  const [types, setTypes] = React.useState<MemoryType[]>([]);
  const [categories, setCategories] = React.useState<MemoryCategory[]>([]);
  const [surfaces, setSurfaces] = React.useState<MemorySurfaceFilter[]>([]);
  const [pinned, setPinned] = React.useState<PinFilter>();
  const [groupBy, setGroupBy] = React.useState<MemoryGroupKey | undefined>();
  const [sortBy, setSortBy] = React.useState<MemorySortKey>();
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [loadMoreError, setLoadMoreError] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [selectedMemoryId, setSelectedMemoryId] = React.useState<string>();
  const [pinningId, setPinningId] = React.useState<string>();
  const [actionError, setActionError] = React.useState<string>();
  const [bulkOperation, setBulkOperation] = React.useState<MemoryBulkOperation>();
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkPromoteOpen, setBulkPromoteOpen] = React.useState(false);
  const [bulkDemoteOpen, setBulkDemoteOpen] = React.useState(false);
  const [bulkResultMessage, setBulkResultMessage] = React.useState<string>();
  const loadingMoreRef = React.useRef(false);
  const filterGenerationRef = React.useRef(0);
  const [updateMemory] = useUpdateMemoryMutation();

  const debouncedSearch = useDebouncedValue(search, 300);
  const filter = React.useMemo(
    () =>
      buildMemorySearchFilter({
        query: debouncedSearch,
        scopes,
        types,
        categories,
        surfaces,
        pinned
      }),
    [debouncedSearch, scopes, types, categories, surfaces, pinned]
  );
  const filterKey = JSON.stringify(filter ?? {});

  const { data, loading, error, fetchMore, refetch, networkStatus } = useSearchMemoriesQuery({
    variables: { first: MEMORY_PAGE_SIZE, filter },
    notifyOnNetworkStatusChange: true
  });

  const connection = data?.searchMemories;
  const memories = React.useMemo(
    () => (connection?.edges ?? []).map((edge) => edge.node),
    [connection?.edges]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor;
  const totalCount = connection?.totalCount ?? 0;
  const initialLoading = loading && !connection;
  const isFetchingMore = loadingMore || networkStatus === NetworkStatus.fetchMore;
  const bulkBusy = !!bulkOperation;
  const manageableMemories = React.useMemo(
    () =>
      memories.filter(
        (memory) =>
          memory.scope === MemoryScope.Agent || (canUpdateOrgMemories && canDeleteOrgMemories)
      ),
    [canDeleteOrgMemories, canUpdateOrgMemories, memories]
  );
  const manageableIds = React.useMemo(
    () => manageableMemories.map((memory) => memory.id),
    [manageableMemories]
  );
  const manageableIdSet = React.useMemo(() => new Set(manageableIds), [manageableIds]);
  const loadedSelectedCount = manageableIds.filter((id) => selection.selectedSet.has(id)).length;
  const allLoadedSelected =
    manageableIds.length > 0 && loadedSelectedCount === manageableIds.length;
  const someLoadedSelected = loadedSelectedCount > 0 && !allLoadedSelected;
  const selectedPersonalIds = memories
    .filter((memory) => memory.scope === MemoryScope.Agent && selection.selectedSet.has(memory.id))
    .map((memory) => memory.id);
  const selectedOrganizationIds = memories
    .filter(
      (memory) => memory.scope === MemoryScope.Organization && selection.selectedSet.has(memory.id)
    )
    .map((memory) => memory.id);
  const demotionCount =
    bulkOperation?.kind === 'demote' ? bulkOperation.ids.length : selectedOrganizationIds.length;
  const promotionCount =
    bulkOperation?.kind === 'promote' ? bulkOperation.ids.length : selectedPersonalIds.length;

  React.useEffect(() => {
    filterGenerationRef.current += 1;
    setLoadMoreError(false);
    setLoadingMore(false);
    setActionError(undefined);
    setBulkDeleteOpen(false);
    setBulkPromoteOpen(false);
    setBulkDemoteOpen(false);
    setBulkResultMessage(undefined);
    selection.clear();
    loadingMoreRef.current = false;
  }, [filterKey, selection.clear]);

  React.useEffect(() => {
    const pending = pendingBulkOperationRef.current;
    if (!pending) return;
    if (bulkFetcher.state !== 'idle') {
      bulkRequestStartedRef.current = true;
      return;
    }
    if (!bulkRequestStartedRef.current) return;

    pendingBulkOperationRef.current = undefined;
    bulkRequestStartedRef.current = false;
    setBulkOperation(undefined);
    setBulkDeleteOpen(false);
    setBulkPromoteOpen(false);
    setBulkDemoteOpen(false);

    const failedIds = bulkFetcher.data?.failedIds ?? [];
    const sameFilter = pending.filterGeneration === filterGenerationRef.current;
    if (sameFilter) selection.setSelected(failedIds);
    if (failedIds.length > 0) {
      const successVerb =
        pending.kind === 'delete'
          ? 'deleted'
          : pending.kind === 'promote' || pending.kind === 'demote'
            ? 'converted'
            : 'updated';
      setBulkResultMessage(
        `${pending.ids.length - failedIds.length} ${successVerb}, ${failedIds.length} failed. ${sameFilter ? 'Failed memories remain selected.' : 'Select the failed memories again to retry.'}`
      );
    } else {
      setBulkResultMessage(undefined);
    }
    void refetch();
  }, [bulkFetcher.data, bulkFetcher.state, refetch, selection.setSelected]);

  const submitBulk = (kind: MemoryBulkOperation['kind'], ids = selection.selected) => {
    if (ids.length === 0 || bulkBusy) return;
    if ((kind === 'promote' || kind === 'demote') && !canUpdateOrgMemories) return;
    const operation: MemoryBulkOperation = {
      kind,
      ids: [...ids],
      filterGeneration: filterGenerationRef.current
    };
    pendingBulkOperationRef.current = operation;
    bulkRequestStartedRef.current = false;
    setBulkOperation(operation);
    setBulkResultMessage(undefined);
    bulkFetcher.submit(
      { memoryIds: operation.ids },
      { method: 'POST', action: `bulk-${kind}`, encType: 'application/json' }
    );
  };

  const toggleAllLoaded = React.useCallback(
    (checked: boolean) => {
      selection.setSelected((current) => {
        if (!checked) return current.filter((id) => !manageableIdSet.has(id));
        return [...new Set([...current, ...manageableIds])];
      });
    },
    [manageableIdSet, manageableIds, selection.setSelected]
  );

  const loadMore = React.useCallback(async () => {
    if (!hasNextPage || !endCursor || loadingMoreRef.current) return;
    const filterGeneration = filterGenerationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      const result = await fetchMore({
        variables: { first: MEMORY_PAGE_SIZE, after: endCursor, filter }
      });
      if (result.error) throw result.error;
    } catch {
      if (filterGeneration === filterGenerationRef.current) setLoadMoreError(true);
    } finally {
      if (filterGeneration === filterGenerationRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [endCursor, fetchMore, filter, hasNextPage]);

  const sentinelRef = useInfiniteScroll(
    () => void loadMore(),
    hasNextPage && !isFetchingMore && !loadMoreError
  );

  const handleSetSticky = React.useCallback(
    async (id: string, sticky: boolean) => {
      if (pinningId) return;
      setPinningId(id);
      setActionError(undefined);
      try {
        const result = await updateMemory({ variables: { input: { id, sticky } } });
        if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
        await refetch();
      } catch (pinError) {
        setActionError(
          pinError instanceof Error
            ? pinError.message
            : 'The pin could not be updated. Please try again.'
        );
      } finally {
        setPinningId(undefined);
      }
    },
    [pinningId, refetch, updateMemory]
  );

  const columns = React.useMemo<
    ColumnDef<MemoryListFieldsFragment, MemorySortKey, MemoryGroupKey>[]
  >(
    () => [
      {
        id: 'select',
        noHref: true,
        cellClassName: 'w-0 py-1.5 px-3',
        renderHeader: () => (
          <TableHeader className="w-0 py-1.5 px-3 text-xs">
            <Checkbox
              aria-label="Select all loaded manageable memories"
              checked={allLoadedSelected}
              indeterminate={someLoadedSelected}
              disabled={manageableIds.length === 0}
              color="sky"
              onChange={toggleAllLoaded}
            />
          </TableHeader>
        ),
        renderCell: (memory) =>
          manageableIdSet.has(memory.id) ? (
            <div>
              <Checkbox
                aria-label={`Select memory: ${memory.content}`}
                checked={selection.selectedSet.has(memory.id)}
                color="sky"
                onChange={(checked) => {
                  selection.setSelected((current) =>
                    checked
                      ? [...new Set([...current, memory.id])]
                      : current.filter((id) => id !== memory.id)
                  );
                }}
              />
            </div>
          ) : null
      },
      {
        id: 'content',
        header: 'Memory',
        cellClassName: 'min-w-72 max-w-xl py-2.5 px-3',
        renderCell: (memory) => (
          <button
            type="button"
            aria-label={`View memory: ${memory.content}`}
            className="line-clamp-3 w-full text-left text-sm text-neutral-900 focus:outline-none focus-visible:underline dark:text-neutral-100"
          >
            {memory.content}
          </button>
        )
      },
      {
        id: 'scope',
        header: 'Scope',
        renderCell: (memory) => <ScopeBadge scope={memory.scope} />
      },
      {
        id: 'type',
        header: 'Type',
        sortKey: 'type',
        groupKey: 'type',
        renderCell: (memory) => enumLabel(memory.type)
      },
      {
        id: 'category',
        header: 'Category',
        headerClassName: 'hidden lg:table-cell',
        cellClassName: 'hidden lg:table-cell py-1.5 px-3',
        renderCell: (memory) => (
          <Badge color={CATEGORY_COLORS[memory.category]}>{enumLabel(memory.category)}</Badge>
        )
      },
      {
        id: 'surface',
        header: 'Surface',
        headerClassName: 'hidden xl:table-cell',
        cellClassName: 'hidden xl:table-cell py-1.5 px-3',
        renderCell: (memory) =>
          memory.surface ? (
            <Badge color={SURFACE_COLORS[memory.surface]}>{enumLabel(memory.surface)}</Badge>
          ) : (
            <Badge color="zinc">Cross-surface</Badge>
          )
      },
      {
        id: 'pinned',
        header: 'Pinned',
        noHref: true,
        align: 'right',
        renderCell: (memory) => {
          const canModify = memory.scope === MemoryScope.Agent || canUpdateOrgMemories;
          const pinLabel = memory.sticky
            ? 'Unpin this memory. Cara may condense or remove it automatically.'
            : 'Pin this memory to protect it from automatic condensing or removal.';
          const controlLabel = canModify
            ? pinLabel
            : 'You do not have permission to pin organization memories.';
          return (
            <button
              type="button"
              disabled={!canModify || pinningId === memory.id}
              aria-label={controlLabel}
              title={controlLabel}
              onClick={() => void handleSetSticky(memory.id, !memory.sticky)}
              className={clsx(
                'rounded-md p-1.5 text-neutral-400 dark:text-neutral-500 transition-colors',
                canModify
                  ? 'hover:bg-neutral-100 hover:text-amber-500 dark:hover:text-amber-400 dark:hover:bg-neutral-700'
                  : 'cursor-not-allowed opacity-40'
              )}
            >
              {memory.sticky ? (
                <PinIconSolid className="size-4 text-amber-500 dark:text-amber-400" />
              ) : (
                <PinIcon className="size-4" />
              )}
            </button>
          );
        }
      }
    ],
    [
      allLoadedSelected,
      pinningId,
      canUpdateOrgMemories,
      handleSetSticky,
      manageableIdSet,
      manageableIds,
      selection.selectedSet,
      selection.setSelected,
      someLoadedSelected,
      toggleAllLoaded
    ]
  );

  const customFilters = React.useMemo<FilterConfig<string>[]>(
    () => [
      {
        label: 'Scope',
        icon: BuildingOffice2Icon,
        multiple: true,
        initialValues: scopes,
        values: [
          { label: 'Your memory', value: MemoryScope.Agent },
          { label: 'Organization', value: MemoryScope.Organization }
        ],
        onChange: (values) => setScopes(values as MemoryScope[])
      },
      {
        label: 'Type',
        icon: TagIcon,
        multiple: true,
        initialValues: types,
        values: Object.values(MemoryType).map((value) => ({ label: enumLabel(value), value })),
        onChange: (values) => setTypes(values as MemoryType[])
      },
      {
        label: 'Category',
        icon: FolderIcon,
        multiple: true,
        initialValues: categories,
        values: Object.values(MemoryCategory).map((value) => ({
          label: enumLabel(value),
          value
        })),
        onChange: (values) => setCategories(values as MemoryCategory[])
      },
      {
        label: 'Surface',
        icon: RectangleStackIcon,
        multiple: true,
        initialValues: surfaces,
        values: [
          ...Object.values(MemorySurface).map((value) => ({
            label: enumLabel(value),
            value
          })),
          { label: 'Cross-surface', value: CROSS_SURFACE }
        ],
        onChange: (values) => setSurfaces(values as MemorySurfaceFilter[])
      },
      {
        label: 'Pinned',
        icon: PinIcon,
        initialValues: pinned ? [pinned] : [],
        values: [
          { label: 'Pinned', value: 'PINNED' },
          { label: 'Not pinned', value: 'UNPINNED' }
        ],
        onChange: (values) => setPinned(values[0] as PinFilter | undefined)
      }
    ],
    [categories, pinned, scopes, surfaces, types]
  );

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-neutral-800">
      <FloatingSelectionBar
        count={selection.selected.length}
        onClear={() => {
          selection.clear();
          setBulkResultMessage(undefined);
        }}
        extraActions={[
          {
            label: 'Pin',
            icon: <PinIconSolid className="size-3.5" />,
            disabled: bulkBusy,
            loading: bulkOperation?.kind === 'pin' && bulkBusy,
            onClick: () => submitBulk('pin')
          },
          ...(canUpdateOrgMemories && promotionCount > 0
            ? [
                {
                  label: 'Make organizational',
                  icon: <BuildingOffice2Icon className="size-3.5" />,
                  disabled: bulkBusy,
                  loading: bulkOperation?.kind === 'promote' && bulkBusy,
                  onClick: () => setBulkPromoteOpen(true)
                }
              ]
            : []),
          ...(canUpdateOrgMemories && demotionCount > 0
            ? [
                {
                  label: 'Make personal',
                  icon: <UserIcon className="size-3.5" />,
                  disabled: bulkBusy,
                  loading: bulkOperation?.kind === 'demote' && bulkBusy,
                  onClick: () => setBulkDemoteOpen(true)
                }
              ]
            : [])
        ]}
        onArchive={() => setBulkDeleteOpen(true)}
        archiveLabel="Delete"
        archiveEnabled={!bulkBusy}
        isArchiving={bulkOperation?.kind === 'delete' && bulkBusy}
      />
      <div className="border-b border-neutral-200 px-4 py-5 sm:px-6 dark:border-neutral-700">
        <div className="mb-4">
          <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-neutral-100">
            Saved memories
          </h3>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Search and organize the preferences, corrections, and procedures Cara can use. Pin
            memories to prevent dreams from consolidating them.
          </p>
        </div>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search memories..."
          customFilters={customFilters}
          groupBy={{
            value: groupBy,
            options: [{ label: 'Type', value: 'type' }],
            onChange: (value) => setGroupBy(value as MemoryGroupKey | undefined)
          }}
        />
      </div>

      {bulkResultMessage && (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 sm:px-6 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <span>{bulkResultMessage}</span>
          <button
            type="button"
            className="font-medium underline"
            onClick={() => setBulkResultMessage(undefined)}
          >
            Dismiss
          </button>
        </div>
      )}

      {initialLoading ? (
        <MemoryPageLoader embedded />
      ) : error && !connection ? (
        <MemoryLoadError onRetry={() => void refetch()} />
      ) : memories.length === 0 ? (
        <MemoryEmptyState filtered={filter !== undefined} />
      ) : (
        <>
          {actionError && (
            <div
              role="alert"
              className="flex items-center justify-between gap-4 bg-rose-50 px-4 py-2 text-sm text-rose-700 sm:px-6 dark:bg-rose-950/30 dark:text-rose-300"
            >
              <span>{actionError}</span>
              <button
                type="button"
                className="font-medium underline"
                onClick={() => setActionError(undefined)}
              >
                Dismiss
              </button>
            </div>
          )}
          {error && (
            <div
              role="alert"
              className="bg-rose-50 dark:bg-rose-500/10 px-4 py-2 text-sm text-rose-700 dark:text-rose-300 sm:px-6"
            >
              Some memories could not be loaded. The results below may be incomplete.
            </div>
          )}
          <div className="overflow-x-auto px-4 sm:px-6">
            <DataTable<MemoryListFieldsFragment, MemorySortKey, MemoryGroupKey>
              data={memories}
              columns={columns}
              keyExtractor={(memory) => memory.id}
              tableClassName="min-w-[46rem] [--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
              sortKey={sortBy}
              sortDirection={sortDirection}
              onSort={(key, direction) => {
                setSortBy(key);
                setSortDirection(direction);
              }}
              sortFn={sortMemories}
              groupKey={groupBy}
              onGroupChange={(key) => setGroupBy(key ?? undefined)}
              groupFn={groupMemoriesByType}
              onRowClick={(memory) => setSelectedMemoryId(memory.id)}
            />
          </div>
          <div className="border-t border-neutral-200 px-4 py-3 text-center dark:border-neutral-700">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Showing {memories.length} of {totalCount} memories
            </p>
            <InfiniteScrollFooter
              hasNextPage={hasNextPage}
              sentinelRef={sentinelRef}
              error={loadMoreError}
              onRetry={() => void loadMore()}
              label="Loading more memories…"
            />
          </div>
        </>
      )}
      {selectedMemoryId && (
        <MemoryDetailDialog
          key={selectedMemoryId}
          memoryId={selectedMemoryId}
          canModifyOrganizationMemories={canUpdateOrgMemories}
          onClose={() => setSelectedMemoryId(undefined)}
          onSaved={async () => {
            await refetch();
          }}
        />
      )}
      <ArchiveConfirmDialog
        open={bulkPromoteOpen}
        count={promotionCount}
        itemNoun="memory"
        itemPlural="memories"
        title="Make memories organizational"
        confirmLabel="Make organizational"
        loading={bulkOperation?.kind === 'promote' && bulkBusy}
        description={
          <>
            Make <strong>{promotionCount}</strong> personal{' '}
            {promotionCount === 1 ? 'memory' : 'memories'} available to everyone in your
            organization? Cara will be able to use {promotionCount === 1 ? 'it' : 'them'} for all
            members.
          </>
        }
        onCancel={() => {
          if (!bulkBusy) setBulkPromoteOpen(false);
        }}
        onConfirm={() => submitBulk('promote', selectedPersonalIds)}
      />
      <ArchiveConfirmDialog
        open={bulkDemoteOpen}
        count={demotionCount}
        itemNoun="memory"
        itemPlural="memories"
        title="Make memories personal"
        confirmLabel="Make personal"
        loading={bulkOperation?.kind === 'demote' && bulkBusy}
        description={
          <>
            Make <strong>{demotionCount}</strong> organization{' '}
            {demotionCount === 1 ? 'memory' : 'memories'} personal to you? Cara will only use{' '}
            {demotionCount === 1 ? 'it' : 'them'} for you, and other members will lose access.
          </>
        }
        onCancel={() => {
          if (!bulkBusy) setBulkDemoteOpen(false);
        }}
        onConfirm={() => submitBulk('demote', selectedOrganizationIds)}
      />
      <ArchiveConfirmDialog
        open={bulkDeleteOpen}
        count={selection.selected.length}
        itemNoun="memory"
        itemPlural="memories"
        title="Delete memories"
        confirmLabel="Delete"
        loading={bulkOperation?.kind === 'delete' && bulkBusy}
        description={
          <>
            Delete <strong>{selection.selected.length}</strong>{' '}
            {selection.selected.length === 1 ? 'memory' : 'memories'}? Cara will stop using{' '}
            {selection.selected.length === 1 ? 'it' : 'them'}. This action cannot be undone.
          </>
        }
        onCancel={() => {
          if (!bulkBusy) setBulkDeleteOpen(false);
        }}
        onConfirm={() => submitBulk('delete')}
      />
    </div>
  );
};

type MemoryDetailDialogProps = {
  memoryId: string;
  canModifyOrganizationMemories: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

export const MemoryDetailDialog = (props: MemoryDetailDialogProps) => {
  const { data, loading, error, refetch } = useMemoryQuery({
    variables: { id: props.memoryId }
  });
  const [updateMemory, { loading: saving }] = useUpdateMemoryMutation();
  const [content, setContent] = React.useState('');
  const [type, setType] = React.useState<MemoryType>(MemoryType.Preference);
  const [category, setCategory] = React.useState<MemoryCategory>(MemoryCategory.General);
  const [surface, setSurface] = React.useState<MemorySurface | null>(null);
  const [contentError, setContentError] = React.useState<string>();
  const [saveError, setSaveError] = React.useState<string>();

  const memory = data?.memory;
  const canModify =
    memory?.scope === MemoryScope.Agent ||
    (memory?.scope === MemoryScope.Organization && props.canModifyOrganizationMemories);

  React.useEffect(() => {
    if (!memory) return;
    setContent(memory.content);
    setType(memory.type);
    setCategory(memory.category);
    setSurface(memory.surface ?? null);
  }, [memory]);

  const handleClose = () => {
    if (!saving) props.onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!memory || !canModify || saving) return;

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setContentError('Memory content cannot be blank.');
      return;
    }
    if (trimmedContent.length > MEMORY_CONTENT_MAX_LENGTH) {
      setContentError(`Memory content must be ${MEMORY_CONTENT_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setContentError(undefined);
    setSaveError(undefined);
    try {
      const result = await updateMemory({
        variables: {
          input: {
            id: memory.id,
            content: trimmedContent,
            type,
            category,
            surface
          }
        }
      });
      if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
      await props.onSaved();
      props.onClose();
    } catch (updateError) {
      setSaveError(
        updateError instanceof Error
          ? updateError.message
          : 'The memory could not be saved. Please try again.'
      );
    }
  };

  return (
    <Dialog open onClose={handleClose} size="2xl">
      <DialogTitle>{canModify ? 'Edit memory' : 'Memory details'}</DialogTitle>
      <DialogDescription>
        {canModify
          ? 'Update what Cara remembers and where this preference applies.'
          : 'You can view this organization memory, but you do not have permission to change it.'}
      </DialogDescription>

      {loading && !memory ? (
        <DialogBody>
          <div className="flex justify-center py-12">
            <Spinner size="size-8" color="neutral" />
          </div>
        </DialogBody>
      ) : error || !memory ? (
        <DialogBody>
          <div role="alert" className="rounded-lg bg-rose-50 p-4 dark:bg-rose-950/30">
            <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
              This memory could not be loaded.
            </p>
            <Button className="mt-3" outline onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </DialogBody>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogBody className="max-h-[65vh] space-y-6 overflow-y-auto pr-1">
            {canModify ? (
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <div className="flex items-center justify-between gap-4">
                      <Label htmlFor="memory-content">Memory content</Label>
                      <span
                        className={clsx(
                          'text-xs tabular-nums',
                          content.length > MEMORY_CONTENT_MAX_LENGTH
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-neutral-500 dark:text-neutral-400'
                        )}
                      >
                        {content.length}/{MEMORY_CONTENT_MAX_LENGTH}
                      </span>
                    </div>
                    <Textarea
                      id="memory-content"
                      rows={6}
                      maxLength={MEMORY_CONTENT_MAX_LENGTH}
                      value={content}
                      invalid={!!contentError}
                      onChange={(event) => {
                        setContent(event.currentTarget.value);
                        setContentError(undefined);
                        setSaveError(undefined);
                      }}
                    />
                    {contentError && <ErrorMessage>{contentError}</ErrorMessage>}
                  </Field>

                  <div className="grid gap-5 sm:grid-cols-3">
                    <Field>
                      <Label htmlFor="memory-type">Type</Label>
                      <Select
                        id="memory-type"
                        value={type}
                        onChange={(event) => {
                          setType(event.currentTarget.value as MemoryType);
                          setSaveError(undefined);
                        }}
                      >
                        {Object.values(MemoryType).map((value) => (
                          <option key={value} value={value}>
                            {enumLabel(value)}
                          </option>
                        ))}
                      </Select>
                    </Field>

                    <Field>
                      <Label htmlFor="memory-category">Category</Label>
                      <Select
                        id="memory-category"
                        value={category}
                        onChange={(event) => {
                          setCategory(event.currentTarget.value as MemoryCategory);
                          setSaveError(undefined);
                        }}
                      >
                        {Object.values(MemoryCategory).map((value) => (
                          <option key={value} value={value}>
                            {enumLabel(value)}
                          </option>
                        ))}
                      </Select>
                    </Field>

                    <Field>
                      <Label htmlFor="memory-surface">Surface</Label>
                      <Select
                        id="memory-surface"
                        value={surface ?? ''}
                        onChange={(event) => {
                          setSurface(
                            event.currentTarget.value
                              ? (event.currentTarget.value as MemorySurface)
                              : null
                          );
                          setSaveError(undefined);
                        }}
                      >
                        <option value="">Cross-surface</option>
                        {Object.values(MemorySurface).map((value) => (
                          <option key={value} value={value}>
                            {enumLabel(value)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            ) : (
              <div>
                <h4 className="text-sm font-medium text-neutral-950 dark:text-white">Memory</h4>
                <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-300">
                  {memory.content}
                </p>
              </div>
            )}

            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-neutral-200 pt-5 text-sm sm:grid-cols-2 dark:border-neutral-700">
              <MemoryDetailItem label="Scope">
                <ScopeBadge scope={memory.scope} />
              </MemoryDetailItem>
              <MemoryDetailItem label="Type">{enumLabel(memory.type)}</MemoryDetailItem>
              <MemoryDetailItem label="Category">{enumLabel(memory.category)}</MemoryDetailItem>
              <MemoryDetailItem label="Surface">
                {memory.surface ? enumLabel(memory.surface) : 'Cross-surface'}
              </MemoryDetailItem>
              <MemoryDetailItem label="Created">
                {formatMemoryDate(memory.createdAt)}
              </MemoryDetailItem>
              <MemoryDetailItem label="Last used">
                {memory.lastAccessedAt ? formatMemoryDate(memory.lastAccessedAt) : 'Not used yet'}
              </MemoryDetailItem>
              <MemoryDetailItem label="Access count">
                {memory.accessCount.toLocaleString()}
              </MemoryDetailItem>
              <MemoryDetailItem label="Retrieval keywords">
                {memory.keywords.length > 0 ? memory.keywords.join(', ') : 'None recorded'}
              </MemoryDetailItem>
            </dl>

            {saveError && (
              <div role="alert" className="rounded-lg bg-rose-50 p-3 dark:bg-rose-950/30">
                <ErrorMessage className="text-sm">{saveError}</ErrorMessage>
              </div>
            )}
          </DialogBody>

          <DialogActions>
            <Button type="button" outline onClick={handleClose} disabled={saving}>
              {canModify ? 'Cancel' : 'Close'}
            </Button>
            {canModify && (
              <Button type="submit" color="sky" loading={saving}>
                Save changes
              </Button>
            )}
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

const MemoryDetailItem = (props: React.PropsWithChildren<{ label: string }>) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
      {props.label}
    </dt>
    <dd className="mt-1 break-words text-neutral-900 dark:text-neutral-100">{props.children}</dd>
  </div>
);

const MemoryPageLoader = ({ embedded = false }: { embedded?: boolean }) => (
  <div className={embedded ? 'px-6' : 'rounded-lg bg-white px-6 shadow dark:bg-neutral-800'}>
    <div className="py-12 text-center">
      <Spinner size="size-8" color="neutral" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
        Loading memories...
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        This might take a moment.
      </p>
    </div>
  </div>
);
